import { PLAN_SELECTION_NOT_PERMITTED_CODE } from '@cadstrom/backend/shared';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import { z } from 'zod';

import { Toast } from '#components/toast/toast.js';
import { useToastStore } from '#state/stores/toast-store.js';
import { useWizardStepAnalytics } from './use-wizard-step-analytics.js';
import { WizardRenderer } from './wizard-renderer.js';

import type { Wizard } from '@cadstrom/cadstrom-shared';

const searchSchema = z.object({
	/** When set, the go-page stays pinned to this plan instead of pulling from the whole queue. */
	planId: z.string().optional(),
});

/**
 * Where a failed claim sends the user.
 *
 * A pinned claim normally returns to the browse queue it came from, and an
 * unpinned one to Wizard home. Losing the access pinning needs is the exception:
 * the browse queue needs that same access, so returning there would fail again.
 *
 * @param planId - The plan the claim was pinned to, or undefined for the whole queue.
 * @param errorCode - The failure code the claim reported.
 * @returns The route to redirect to.
 */
function redirectTargetForFailedClaim(
	planId: string | undefined,
	errorCode: string | null,
): '/wizard' | '/wizard/queue' {
	if (planId === undefined || errorCode === PLAN_SELECTION_NOT_PERMITTED_CODE) {
		return '/wizard';
	}
	return '/wizard/queue';
}

export const Route = createFileRoute('/_authorization-gate/wizard/go')({
	staticData: {
		authorize: (ability) => ability.can('manage', 'wizard'),
	},
	validateSearch: searchSchema,
	beforeLoad: ({ context: { trpc }, search: { planId } }) => {
		// `getNext` pulls from the whole queue, or stays pinned to a single plan when `planId`
		// is set. `select` narrows to the success member (or redirects) so the component sees a
		// guaranteed step.
		return {
			planId,
			getNextStepOptions: trpc.wizard.step.getNext.queryOptions(
				{ planId },
				{
					select: (data) => {
						if (!data.isSuccess) {
							throw redirect({ to: redirectTargetForFailedClaim(planId, data.errorCode) });
						}
						return data;
					},
				},
			),
		};
	},
	loader: async ({ context: { trpc, queryClient, getNextStepOptions, planId } }) => {
		// `getNext` is a side-effecting claim, not a pure read: it must hit the server on every navigation
		// into the go-page. Serving it from the default-2-minute cache lets a just-released (or reassigned)
		// claim resolve against stale state, bouncing the user back to a plan they no longer hold.
		const { isSuccess, errorCode, errorMessage } = await queryClient.fetchQuery({
			...getNextStepOptions,
			staleTime: 0,
		});
		if (!isSuccess) {
			const noSteps = errorCode === 'NO_AVAILABLE_STEPS';
			const pinNotPermitted = errorCode === PLAN_SELECTION_NOT_PERMITTED_CODE;

			if (pinNotPermitted) {
				useToastStore.getState().addToast(
					<Toast severity='warning' title='You cannot work a specific plan'>
						Your access no longer covers choosing a plan to work. You can still pick up the next available task.
					</Toast>,
				);
				// The link that produced this URL came from an ability map fetched before the access
				// was withdrawn. Re-read it so the surfaces that offer a pinned claim stop being offered.
				await queryClient.invalidateQueries({ queryKey: trpc.user.getCurrent.queryKey() });
			} else {
				useToastStore.getState().addToast(
					<Toast severity='info' title={planId === undefined ? 'No available tasks' : 'Plan complete'}>
						{noSteps ?
							planId === undefined ?
								'There are no available tasks to work on right now.'
							:	"You've finished the available work on this plan."
						:	errorMessage}
					</Toast>,
				);
			}

			if (noSteps && planId === undefined) {
				// Drop the count the home page cached before this claim attempt: the attempt just proved
				// it wrong, and the user is about to land back on that page. Zero is a placeholder to
				// render against, not an assertion — the home page re-reads the real count on mount.
				const countQueryKey = trpc.wizard.step.getAvailablePlanCount.queryKey({});
				// A count fetch the home page started before the user clicked through is still in flight
				// and would resolve into the cache after this write, restoring the number the claim just
				// disproved. Cancel it first so the placeholder is what the page paints.
				await queryClient.cancelQueries({ queryKey: countQueryKey });
				queryClient.setQueryData(countQueryKey, {
					isSuccess: true,
					count: 0,
				});
			}

			throw redirect({ to: redirectTargetForFailedClaim(planId, errorCode) });
		}
	},
	head: () => ({ meta: [{ title: 'Wizard • Cadstrom' }] }),
	component: WizardGoPage,
});

function WizardGoPage() {
	const { getNextStepOptions, planId } = Route.useRouteContext();

	const {
		data: { step: initialStep, claimExpiresAt: initialClaimExpiresAt, context: initialContext },
	} = useSuspenseQuery(getNextStepOptions);

	const [currentStep, setCurrentStep] = useState<Wizard.Step>(initialStep);
	const [stepIdHistory, setStepIdHistory] = useState<string[]>([initialStep.id]);
	// `claimExpiresAt` lives up here instead of inside `WizardRenderer` because `WizardRenderer`'s state gets reset when
	// the step changes, and the claim expiry time needs to persist across steps.
	const [claimExpiresAt, setClaimExpiresAt] = useState<number>(initialClaimExpiresAt);
	const [stepContext, setStepContext] = useState<Wizard.WizardStepContext | undefined>(initialContext);

	useWizardStepAnalytics(currentStep, stepContext);

	const handleNewStep = useCallback(
		({
			step: newStep,
			claimExpiresAt: newClaimExpiresAt,
			context: newContext,
		}: {
			step: Wizard.Step;
			claimExpiresAt: number;
			context?: Wizard.WizardStepContext;
		}) => {
			setStepIdHistory((prev) => [...prev, newStep.id]);

			setClaimExpiresAt(newClaimExpiresAt);
			setStepContext(newContext);
			setCurrentStep(newStep);
		},
		[setCurrentStep],
	);

	// When going back to a previous step, the backend will actually send us a new step (with a new step ID) with the same
	// data as the previous step. This function "rewrites history" so that the technically-new step replaces the old one.
	const handleReviseStep = useCallback(
		(
			oldStepId: string,
			{
				step: newStep,
				claimExpiresAt: newClaimExpiresAt,
				context: newContext,
			}: { step: Wizard.Step; claimExpiresAt: number; context?: Wizard.WizardStepContext },
		) => {
			setStepIdHistory((prev) => {
				const indexOfPrev = prev.findIndex((id) => id === oldStepId);
				if (indexOfPrev === -1) {
					return prev;
				}
				return prev.slice(0, indexOfPrev).concat(newStep.id);
			});

			setClaimExpiresAt(newClaimExpiresAt);
			setStepContext(newContext);
			setCurrentStep(newStep);
		},
		[],
	);

	return (
		<WizardRenderer
			key={currentStep.id}
			step={currentStep}
			planId={planId}
			packageId={stepContext?.packageId ?? undefined}
			previousStepId={stepIdHistory.at(-2)}
			claimExpiresAt={claimExpiresAt}
			setClaimExpiresAt={setClaimExpiresAt}
			handleNewStep={handleNewStep}
			handleReviseStep={handleReviseStep}
		/>
	);
}
